import { Controller } from "@hotwired/stimulus"
import jsQR from "jsqr"

export default class extends Controller {
  static targets = ["video", "canvas", "glyph", "status", "toggleButton"]
  static values = {
    thingId: String,
    autostart: { type: Boolean, default: false }
  }

  connect() {
  this.SCAN_INTERVAL = 5000
  this.lastScanTime = 0
  this.statusResetTimer = null
  this.video = this.videoTarget
  this.canvasElement = this.canvasTarget
  this.canvas = this.canvasElement.getContext("2d", { willReadFrequently: true })
  this.statusBox = this.statusTarget
  this.glyphPreview = this.glyphTarget
  this.stream = null
  this.rafId = null

  this.fetchRedemptionsLog()

  if (this.autostartValue) {
    this.start()
  } else {
    this.updateStatus("🍓 Scanner idle. Click Turn Scanner On.")
    this.updateButtons(false)
  }
}

disconnect() {
  this.stop()
}

start() {
  if (this.stream) return
  this.updateStatus("🍓 Starting scanner...")
  this.updateButtons(true)
  this.startCamera()
}

toggle() {
  if (this.stream) {
    this.stop()
  } else {
    this.start()
  }
}

stop() {
  window.clearTimeout(this.statusResetTimer)
  this.statusResetTimer = null
  if (this.rafId) {
    cancelAnimationFrame(this.rafId)
    this.rafId = null
  }
  if (this.stream) {
    this.stream.getTracks().forEach(track => track.stop())
    this.stream = null
  }
  if (this.video) {
    this.video.srcObject = null
  }
  this.updateStatus("🍓 Scanner stopped.")
  this.updateButtons(false)
}

  startCamera() {
    navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } })
      .then(stream => {
        this.stream = stream
        this.video.srcObject = stream
        this.video.setAttribute("playsinline", true)
        this.video.muted = true
        this.video.play()
  
        this.video.addEventListener("loadeddata", () => {
          console.log("📸 Video stream loaded and ready.")
          this.updateStatus("🍓 Scanning...")
          this.rafId = requestAnimationFrame(this.scanFrame.bind(this))
        })
      })
      .catch(err => {
        this.updateStatus("🚫 Could not access camera.")
        this.updateButtons(false)
        console.error("Camera error:", err)
      })
  }
  

  scanFrame() {
    if (this.video.readyState === this.video.HAVE_ENOUGH_DATA) {
      this.canvasElement.height = this.video.videoHeight
      this.canvasElement.width = this.video.videoWidth
      this.canvas.drawImage(this.video, 0, 0, this.canvasElement.width, this.canvasElement.height)
      const imageData = this.canvas.getImageData(0, 0, this.canvasElement.width, this.canvasElement.height)
      const code = jsQR(imageData.data, imageData.width, imageData.height, { inversionAttempts: "dontInvert" })

      const now = Date.now()
      if (code && now - this.lastScanTime > this.SCAN_INTERVAL) {
        this.lastScanTime = now
        const scannedCode = code.data.toUpperCase().trim()
        if (this.glyphPreview) this.glyphPreview.innerText = this.convertToSymbols(scannedCode)
        this.updateStatus("🔍 Verifying...")
        this.verifyStadiumCode(scannedCode)
      }
    }
    this.rafId = requestAnimationFrame(this.scanFrame.bind(this))
  }

  convertToSymbols(code) {
    const BASE11_SYMBOLS = {
      "0": "🛸", "1": "🌚", "2": "☄️", "3": "🐦‍⬛",
      "4": "🐳", "5": "🐇", "6": "🪙", "7": "🍦",
      "8": "⚪", "9": "🌝"
    }
    return code.split("").map(char => BASE11_SYMBOLS[char] || char).join(" ")
  }

  verifyStadiumCode(code) {
    fetch("/ownerships/verify_stadium", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("[name='csrf-token']").content
      },
      body: JSON.stringify({ code: code, thing_id: this.thingIdValue })
    })
      .then(r => r.json())
      .then(data => {
        if (!this.statusBox) return
        this.lastScanTime = Date.now()
        if (data.status === "ok") {
          const match = data.matches[0]
          let amount = match.available
  
          const isSpent = amount <= 0
  
          this.statusBox.innerHTML = `
            <div class="glow-${isSpent ? "fail" : "success"}">
              ${isSpent ? "💨🔥 CODE SPENT" : "🍓 CODE ACCEPTED!"}
            </div>
            <div><black>${match.alias} (<small>${match.user_id}</small>)</black></div>
            <div><black><small>🎟️HODL:</small> ${parseFloat(match.amount_to_own).toFixed(0)}</black></div>
            <div><black><small>🕳️SPENT:</small> ${parseFloat(match.redeemed).toFixed(0)}</black></div>
            <div><black><small>🕊️░▒▓ ${parseFloat(match.available).toFixed(0)}</small></black></div>
  
            ${!isSpent ? `
            <div style="margin-top: 1.25rem; text-align: center;">
              <label><small>ADJUST</small></label><br>
              
              <button id="decr" style="font-size: 1.5rem; padding: 0.5rem 1rem; margin: 0 0.5rem;">−</button>
              <span id="amt-val" style="font-weight: bold; font-size: 1.2rem;">(${Math.round(amount)})</span>
              <button id="incr" style="font-size: 1.5rem; padding: 0.5rem 1rem; margin: 0 0.5rem;">+</button>
  
              <div style="margin-top: 1rem;">
                <button id="confirm-redeem" style="font-size: 1.2rem; padding: 0.6rem 1.5rem;">✅ Confirm</button>
              </div>
            </div>
            ` : ""}
          `
  
          if (!isSpent) {
            document.getElementById("incr")?.addEventListener("click", () => {
              if (amount < match.available) {
                amount++
                document.getElementById("amt-val").innerText = `(${amount})`
              }
            })
  
            document.getElementById("decr")?.addEventListener("click", () => {
              if (amount > 1) {
                amount--
                document.getElementById("amt-val").innerText = `(${amount})`
              }
            })
  
            document.getElementById("confirm-redeem")?.addEventListener("click", () => {
              this.confirmRedemption(match.ownership_id, amount)
            })
          }
        } else {
          this.statusBox.innerText = `🚫 ${data.message}`
        }
      })
      .catch(err => {
        console.error(err)
        if (this.statusBox) this.statusBox.innerText = "❌ Verification failed."
      })
  }
  

  confirmRedemption(ownershipId, amount) {
    fetch("/ownerships/redeem", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("[name='csrf-token']").content
      },
      body: JSON.stringify({ id: ownershipId, amount: amount })
    })
      .then(r => r.json())
      .then(data => {
        if (!this.statusBox) return
        this.lastScanTime = Date.now()
        window.clearTimeout(this.statusResetTimer)
        if (data.status === "ok") {
          this.statusBox.innerText = `✅ Redeemed ${amount}`
          this.fetchRedemptionsLog()
          this.statusResetTimer = window.setTimeout(() => {
            this.statusResetTimer = null
            if (this.statusBox) this.statusBox.innerText = "🎯 Scanning..."
          }, 5000)
        } else {
          this.statusBox.innerText = `❌ Redemption failed: ${data.message}`
        }
      })
      .catch(err => {
        console.error(err)
        if (this.statusBox) this.statusBox.innerText = "❌ Redemption network error"
      })
  }

  fetchRedemptionsLog() {
    fetch(`/ownerships/redeemed_for_thing?thing_id=${this.thingIdValue}`)
      .then(r => r.json())
      .then(data => {
        const list = document.getElementById("redeemed-db-list")
        if (!list) return

        list.innerHTML = ""
        data.forEach(entry => {
          const li = document.createElement("li")
          const isFull = entry.amount_redeemed >= entry.amount_total
          li.style.opacity = isFull ? 0.5 : 1
          li.style.color = isFull ? "#999" : "#000"
          li.innerHTML = `🧍 <strong>${entry.alias}</strong> redeemed ${entry.amount_redeemed} / ${parseFloat(entry.amount_total).toFixed(0)} <span style="float:right;">${entry.updated_at}</span>`
          list.appendChild(li)
        })
      })
      .catch(err => console.error("❌ Could not load redemptions log", err))
  }

  updateStatus(message) {
    window.clearTimeout(this.statusResetTimer)
    this.statusResetTimer = null
    if (!this.statusBox) return
    this.statusBox.innerHTML = `<div class="status-line">${message}</div>`
  }

updateButtons(active) {
    if (!this.hasToggleButtonTarget) return
    this.toggleButtonTarget.textContent = active ? "Turn Scanner Off" : "Turn Scanner On"
    this.toggleButtonTarget.setAttribute("aria-pressed", active ? "true" : "false")
  }
}
